import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Language } from '../../context/LanguageContext';
import './ContactModal.css';

interface ContactModalTranslations {
    title: string;
    visitStore: {
        title: string;
        description: string;
        buttonText: string;
    };
    callUs: {
        title: string;
        description: string;
    };
    email?: {
        title: string;
        description: string;
    };
}

interface ContactModalBaseProps {
    isOpen: boolean;
    onClose: () => void;
    translations: ContactModalTranslations;
    phoneNumber: string;
    emailAddress?: string;
}

function ContactModalBase({ isOpen, onClose, translations, phoneNumber, emailAddress }: ContactModalBaseProps) {
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
            // Disable scroll when modal is open
            document.body.style.overflow = 'hidden';
            // Add padding right to prevent layout shift when scrollbar disappears
            document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
        } else {
            // Re-enable scroll when modal is closed
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
        }

        // Cleanup function
        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center overflow-y-auto"
            onClick={handleOverlayClick}
        >
            <div className="bg-white rounded-lg p-8 max-w-md w-full m-4 relative animate-modalEntry">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <h2 className="text-2xl font-light mb-6">{translations.title}</h2>

                <div className="space-y-6">
                    <div className="p-6 border border-gray-200 rounded-lg hover:border-black transition-colors cursor-pointer">
                        <h3 className="text-lg font-medium mb-2">{translations.visitStore.title}</h3>
                        <p className="text-gray-600 mb-4">{translations.visitStore.description}</p>
                        <button
                            onClick={() => {
                                navigate('/stores/stores');
                                onClose();
                            }}
                            className="text-black underline hover:text-gray-600 transition-colors"
                        >
                            {translations.visitStore.buttonText}
                        </button>
                    </div>

                    <div className="p-6 border border-gray-200 rounded-lg hover:border-black transition-colors cursor-pointer">
                        <h3 className="text-lg font-medium mb-2">{translations.callUs.title}</h3>
                        <p className="text-gray-600 mb-4">{translations.callUs.description}</p>
                        <a href={`tel:${phoneNumber}`} className="text-black underline hover:text-gray-600 transition-colors">{phoneNumber}</a>
                    </div>

                    {emailAddress && translations.email && (
                        <div className="p-6 border border-gray-200 rounded-lg hover:border-black transition-colors cursor-pointer">
                            <h3 className="text-lg font-medium mb-2">{translations.email.title}</h3>
                            <p className="text-gray-600 mb-4">{translations.email.description}</p>
                            <a href={`mailto:${emailAddress}`} className="text-black underline hover:text-gray-600 transition-colors">{emailAddress}</a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ContactModalBase; 